import React, { useCallback, useMemo } from 'react'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { setGeoRedirect, Status } from 'storage/geo'
import { connect } from 'react-redux'
import setRegionCookie from 'cookies/setRegionCookie'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import TextButton from 'components/Luxkit/Button/TextButton'
import Flag from 'components/Luxkit/Flag'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import { RouterLocation } from 'connected-react-router'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import config from 'constants/config'
import TextLink from 'components/Luxkit/TextLink'
import { AGENTHUB_SUPPORT_EMAIL } from 'agentHub/constants'

interface Props {
  accountRegionCode: string;
  currentRegionCode: string;
  location: RouterLocation<unknown>;
}

function GeoRedirectModal(props: Props) {
  const { accountRegionCode, currentRegionCode, location } = props
  const modalContext = useModalElementContext()

  const accountRegion = useMemo(() => config.regions.find(region => region.code === accountRegionCode)!, [accountRegionCode])
  const currentRegion = useMemo(() => config.regions.find(region => region.code === currentRegionCode)!, [currentRegionCode])
  const onRegionChange = useCallback(() => {
    setRegionCookie(accountRegionCode)
    setGeoRedirect(Status.redirected)
    modalContext.resolve()
    window.location.href = location.pathname.replace(/^\/[a-z]+/, '/' + accountRegionCode.toLowerCase()) + location.search
  }, [accountRegionCode, location, modalContext])

  const onIgnoreClick = useCallback(() => {
    setRegionCookie(currentRegionCode)
    setGeoRedirect(Status.ignore)
    modalContext.resolve()
  }, [currentRegionCode, modalContext])

  const agentHubEnabled = config.agentHub.isEnabled

  return (
    <ModalBase
      size="S"
      dismissible={!agentHubEnabled}
      onClose={onIgnoreClick}
    >
      <ModalHeader
        dismissible={!agentHubEnabled}
        title={agentHubEnabled ? "It looks like you're in the wrong place" : 'Are you in the right place?'}
      />
      <ModalBody>
        <ModalContent>
          <BodyText variant="large">
            Looks like you're based in {accountRegion.name}, please select the correct site to browse exclusive offers in your local currency ({accountRegion.currencyCode}).
          </BodyText>
        </ModalContent>
      </ModalBody>
      <ModalFooter actionsLayout="stacked">
        <TextButton
          startIcon={<Flag countryCode={accountRegion.code} />}
          kind="secondary"
          variant="dark"
          fit="full-width"
          size="large"
          onClick={onRegionChange}
        >
          Go to {accountRegion.name} site
        </TextButton>
        {!agentHubEnabled && (
          <TextButton
            startIcon={<Flag countryCode={currentRegion?.code } />}
            kind="secondary"
            variant="dark"
            fit="full-width"
            size="large"
            data-testid="ignore-geo-redirect-modal"
            onClick={onIgnoreClick}
          >
            Stay on {currentRegion?.name} site
          </TextButton>
        )}
        {agentHubEnabled && (
          <>
            <BodyText variant="medium">
              If you think you're in the right place, please contact our support team.
            </BodyText>
            <TextLink href={`mailto:${AGENTHUB_SUPPORT_EMAIL}`}>{AGENTHUB_SUPPORT_EMAIL}</TextLink>
          </>
        )}
      </ModalFooter>
    </ModalBase>
  )
}

function mapStateToProps(state: App.State) {
  return {
    accountRegionCode: state.auth.account.countryCode,
    currentRegionCode: state.geo.currentRegionCode,
    location: state.router.location,
  }
}

export default connect(mapStateToProps)(GeoRedirectModal)
